import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldValues, Path, useForm, UseFormHandleSubmit, UseFormRegisterReturn } from 'react-hook-form';

import { Utils } from '@treezor/design-system';

import { useCardContext } from '../context/CardContext';

import {
  TCardContext,
  limitPeriod,
  limitTypes,
  TCardLimits,
  TLimitPeriod,
  TLimitType,
} from '@/features/cards/store/models/card.model';

import { useAppSelector } from '@/hooks/redux-helpers';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import { filterLimits, getLimitValue } from './useInitializeCard';

export interface IField {
  type: TLimitType;
  period: TLimitPeriod;
  key: keyof TCardLimits;
}
export interface IBlockLimits<T extends FieldValues> extends IField {
  label: string;
  name: Path<T>;
  rules?: { validate: () => string | true };
  consumed?: number;
  limit?: number;
  progressInfo?: {
    label: string;
    value: number;
  };
  isEnabled: boolean;
  props: UseFormRegisterReturn;
}
export interface IFormStatusType {
  hasErrors: boolean;
  isDirty: boolean;
  handleSubmit?: UseFormHandleSubmit<FieldValues>;
  getValues?: () => FieldValues;
  enabledFields?: (keyof TCardLimits)[];
}

export const initialLimitFormStatus = {
  hasErrors: false,
  isDirty: false,
  isValid: true,
  errors: {},
  values: {},
};

function getLimitKey(type: TLimitType, period: TLimitPeriod, prefix: string = 'limit') {
  return `${prefix}${Utils.capitalize(type)}${Utils.capitalize(period)}`;
}

export function useCardsLimitsFields(context: TCardContext, filterFields: (keyof TCardLimits)[] = []) {
  if (!context) {
    context = 'CARD';
  }

  const config = useDashboardConfig();
  const { t } = useTranslation();
  const { card, designType, cardPrint, status } = useCardContext(context !== 'CARD');
  const [, setRenderCount] = useState(0);
  const bulkCardCreationDraft = useAppSelector((state) => state.bulkCardCreation.bulkCardCreationDraft);

  const fields: IField[] = [
    ...Object.keys(limitTypes).reduce((prev, type) => {
      return [
        ...prev,
        ...Object.keys(limitPeriod).map((period) => ({
          type,
          period,
          key: getLimitKey(type as TLimitType, period as TLimitPeriod),
        })),
      ];
    }, []),
  ].filter((field) => filterFields.length === 0 || filterFields.includes(field.key));

  const currentDesign = config?.settings.cards.designs.find((design) => {
    if (context === 'BULK') {
      return design.value.toString() === bulkCardCreationDraft.cardPrint;
    } else {
      if (cardPrint) {
        return design.value === cardPrint;
      } else if (designType) {
        return design.type === designType;
      }
    }
  });

  const cardValues = card
    ? fields.reduce(
        (prev, current) => ({
          [getLimitKey(current.type, current.period)]:
            card[`limit${Utils.capitalize(current.type)}${Utils.capitalize(current.period)}`] || '0',
          ...prev,
        }),
        {},
      )
    : undefined;
  const defaultValues = fields.reduce((prev, current) => {
    const configValue = currentDesign?.limits?.[current.type][`default${Utils.capitalize(current.period)}`];
    const key = getLimitKey(current.type, current.period);
    return {
      [key]: context === 'BULK' ? bulkCardCreationDraft[key] || configValue : configValue,
      ...prev,
    };
  }, {});
  const maxValues = fields.reduce(
    (prev, current) => ({
      [getLimitKey(current.type, current.period)]:
        currentDesign?.limits?.[current.type][`max${Utils.capitalize(current.period)}`],
      ...prev,
    }),
    {},
  );

  const [enabledFields, setEnabledFields] = useState<(keyof TCardLimits)[]>(
    fields.map((field) => field.key as keyof TCardLimits).filter((key) => context !== 'CARD' || cardValues[key] > 0),
  );

  const normalizeValue = useCallback(
    (key, value): number => {
      const isEnabled = enabledFields.includes(key);
      if (!value) {
        return 0;
      } else {
        if (isEnabled) {
          return parseInt(value);
        } else {
          return 0;
        }
      }
    },
    [enabledFields],
  );

  const {
    getValues,
    formState: { isDirty, dirtyFields },
    setValue,
    handleSubmit: submit,
    register,
  } = useForm<TCardLimits>({
    defaultValues: context !== 'CARD' ? defaultValues : cardValues,
    mode: 'onChange',
  });

  const generateLimit = useCallback(
    (type: TLimitType, period: TLimitPeriod): IBlockLimits<TCardLimits> => {
      const limitKey = `limit${Utils.capitalize(type)}${Utils.capitalize(period)}` as keyof TCardLimits;
      const consumed = getLimitValue({ limits: card, prefix: 'total' }, type, period) || 0;
      const value = getValues(limitKey);
      const limit = value !== undefined ? value : card?.[limitKey] || defaultValues[limitKey] || 0;

      return {
        type,
        period,
        name: limitKey,
        key: limitKey,
        label: t(`pages.currentProfiles.cards.settings.limits.${type}.${period}`),
        consumed,
        limit: limit > 0 ? limit : 0,
        isEnabled: enabledFields.indexOf(limitKey) > -1,
        progressInfo: {
          label: t(`pages.currentProfiles.cards.settings.limits.infos.${period}`),
          value: (consumed * 100) / limit,
        },
        props: register(limitKey),
      };
    },
    [t, card, enabledFields, getValues, defaultValues, register],
  );

  const limitsFields: IBlockLimits<TCardLimits>[] = fields.map((block: { type: TLimitType; period: TLimitPeriod }) =>
    generateLimit(block.type, block.period),
  );

  const errors = Object.keys(limitTypes).reduce((result, type: TLimitType) => {
    const allFieldsValues = limitsFields.reduce((prev, current) => {
      return {
        [current.key]: current.limit,
        ...prev,
      };
    }, {});

    const allTypeValues = filterLimits(allFieldsValues, type, ['day', 'week', 'month']);
    const isAllNull = Object.keys(allTypeValues).every(
      (key) => allTypeValues[key] === 0 || !limitsFields.find((field) => field.key === key)?.isEnabled,
    );

    const fieldsErrors = Object.keys(allFieldsValues).reduce((prev, limitKey) => {
      const fieldValue = allFieldsValues[limitKey];
      let error: string | undefined = undefined;

      const field = limitsFields.filter((field) => field.key === limitKey);

      const isEnabled = field.length === 1 && field[0].isEnabled;

      if (fieldValue > maxValues[limitKey] && isEnabled) {
        error = t('pages.currentProfiles.cards.settings.limits.error.overLimit');
      } else if (fieldValue === 0 && fieldValue != 'year' && fieldValue != 'all' && isAllNull) {
        error = t(`pages.currentProfiles.cards.settings.limits.error.${type}`);
      }
      return {
        [limitKey]: error,
        ...prev,
      };
    }, {});

    if (isAllNull) {
      return {
        [type]: t(`pages.currentProfiles.cards.settings.limits.error.${type}`),
        ...fieldsErrors,
        ...result,
      };
    } else {
      return { ...fieldsErrors, ...result };
    }
  }, {});

  const hasErrors: boolean = !!errors && Object.keys(errors).filter((field) => errors[field] != undefined).length > 0;

  const toggleField = useCallback(
    (fieldName, forceValue?: boolean) => {
      if (forceValue !== undefined) {
        setEnabledFields((prev) => {
          if (forceValue) {
            return [...prev, fieldName];
          } else {
            return prev.filter((field) => field !== fieldName);
          }
        });
      } else {
        setEnabledFields((prev) => {
          const existed = prev.indexOf(fieldName);
          return existed > -1 ? prev.filter((field) => field !== fieldName) : [...prev, fieldName];
        });
      }
    },
    [setEnabledFields],
  );

  const handleChange = useCallback(
    (key: keyof TCardLimits, value: string | number | boolean, eventType: 'blur' | 'change' = 'change') => {
      const intValue = parseInt(value.toString());
      setValue(key, intValue, { shouldDirty: true });
      setRenderCount((prev) => prev + 1);
      if (value === 0 && eventType === 'blur') {
        toggleField(key, false);
      }
    },
    [setValue, toggleField],
  );

  const handleSubmit = useCallback(
    (submitFunction: (limits: TCardLimits<string | number>) => void) => {
      return submit((values) => {
        const nomalizedValues = Object.keys(values).reduce((prev, current: keyof TCardLimits) => {
          return {
            [current]: normalizeValue(current, values[current]),
            ...prev,
          };
        }, {});
        submitFunction(nomalizedValues);
      });
    },
    [submit, normalizeValue],
  );

  return {
    limits: getValues(),
    hasErrors,
    isDirty,
    limitsFields,
    onChange: handleChange,
    toggleField,
    dirtyFields,
    status,
    handleSubmit,
    getValues,
    errors,
    enabledFields,
    normalizeValue,
  };
}
